import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Plus, Trash2, AlertCircle } from 'lucide-react';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOAL_CATEGORIES = ['生活', '就労', '健康', '社会参加', 'コミュニケーション', '余暇活動', 'その他'];

export default function SupportPlanFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [facilities, setFacilities] = useState([]);
  
  const [formData, setFormData] = useState({
    client_id: '',
    facility_id: '',
    plan_period_start: new Date().toISOString().split('T')[0],
    plan_period_end: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    service_type: '日中一時支援',
    assessment_summary: '',
    client_wishes: '',
    family_wishes: '',
    goals: [{
      category: '生活',
      short_term_goal: '',
      long_term_goal: '',
      support_content: '',
      frequency: '',
      responsible_person: ''
    }],
    weekly_schedule: '',
    special_notes: '',
    created_by: '',
    created_by_name: ''
  });

  const serviceTypes = ['日中一時支援', '移動支援', '地域生活支援'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        created_by: user.id,
        created_by_name: user.name,
        facility_id: user.facility_id || prev.facility_id
      }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [clientsRes, facilitiesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/facilities`)
      ]);
      setClients(clientsRes.data);
      setFacilities(facilitiesRes.data);
      
      if (facilitiesRes.data.length > 0 && !formData.facility_id) {
        setFormData(prev => ({ ...prev, facility_id: facilitiesRes.data[0].id }));
      }
    } catch (error) {
      console.error('データの取得に失敗しました', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGoalChange = (index, field, value) => {
    setFormData(prev => {
      const newGoals = [...prev.goals];
      newGoals[index] = { ...newGoals[index], [field]: value };
      return { ...prev, goals: newGoals };
    });
  };

  const addGoal = () => {
    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, {
        category: '生活',
        short_term_goal: '',
        long_term_goal: '',
        support_content: '',
        frequency: '',
        responsible_person: ''
      }]
    }));
  };

  const removeGoal = (index) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.client_id) {
      setError('利用者を選択してください');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/support-plans`, formData);
      navigate(`/support-plans/${response.data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="support-plan-form-page">
      {/* ヘッダー */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/support-plans')}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            個別支援計画作成
          </h1>
          <p className="text-muted-foreground mt-1">
            新しい個別支援計画を作成します
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div 
            className="flex items-center gap-2 p-4 rounded-lg bg-[hsl(var(--error-bg))] text-[hsl(var(--error))]"
            data-testid="form-error-message"
          >
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* 基本情報 */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle>基本情報</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>利用者 *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(value) => handleChange('client_id', value)}
              >
                <SelectTrigger data-testid="plan-client-select">
                  <SelectValue placeholder="利用者を選択" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} ({c.name_kana})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>事業所</Label>
              <Select 
                value={formData.facility_id} 
                onValueChange={(value) => handleChange('facility_id', value)}
              >
                <SelectTrigger data-testid="plan-facility-select">
                  <SelectValue placeholder="事業所を選択" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>サービス種別 *</Label>
              <Select 
                value={formData.service_type} 
                onValueChange={(value) => handleChange('service_type', value)}
              >
                <SelectTrigger data-testid="plan-service-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>計画期間開始 *</Label>
              <Input
                type="date"
                value={formData.plan_period_start}
                onChange={(e) => handleChange('plan_period_start', e.target.value)}
                required
                data-testid="plan-start-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label>計画期間終了 *</Label>
              <Input
                type="date"
                value={formData.plan_period_end}
                onChange={(e) => handleChange('plan_period_end', e.target.value)}
                required
                data-testid="plan-end-date-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* アセスメント・希望 */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle>アセスメント・本人の希望</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>アセスメント要約 *</Label>
              <Textarea
                value={formData.assessment_summary}
                onChange={(e) => handleChange('assessment_summary', e.target.value)}
                placeholder="利用者の現状、ニーズ、強み、課題などを記入"
                rows={4}
                required
                data-testid="plan-assessment-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>本人の希望 *</Label>
              <Textarea
                value={formData.client_wishes}
                onChange={(e) => handleChange('client_wishes', e.target.value)}
                placeholder="利用者本人の希望や目標を記入"
                rows={3}
                required
                data-testid="plan-client-wishes-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>家族の希望</Label>
              <Textarea
                value={formData.family_wishes}
                onChange={(e) => handleChange('family_wishes', e.target.value)}
                placeholder="家族の希望や要望を記入"
                rows={3}
                data-testid="plan-family-wishes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* 目標設定 */}
        <Card className="card-surface">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>支援目標</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addGoal}>
              <Plus size={14} className="mr-1" />
              目標追加
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {formData.goals.map((goal, index) => (
              <div key={index} className="p-4 border border-border rounded-lg space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">目標 {index + 1}</h4>
                  {formData.goals.length > 1 && (
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="icon"
                      className="text-destructive"
                      onClick={() => removeGoal(index)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>カテゴリ</Label>
                    <Select 
                      value={goal.category} 
                      onValueChange={(value) => handleGoalChange(index, 'category', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GOAL_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>担当者</Label>
                    <Input
                      value={goal.responsible_person}
                      onChange={(e) => handleGoalChange(index, 'responsible_person', e.target.value)}
                      placeholder="担当者名"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label>短期目標</Label>
                    <Input
                      value={goal.short_term_goal}
                      onChange={(e) => handleGoalChange(index, 'short_term_goal', e.target.value)}
                      placeholder="3ヶ月以内に達成を目指す具体的な目標"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label>長期目標</Label>
                    <Input
                      value={goal.long_term_goal}
                      onChange={(e) => handleGoalChange(index, 'long_term_goal', e.target.value)}
                      placeholder="計画期間内に達成を目指す目標"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label>支援内容</Label>
                    <Textarea
                      value={goal.support_content}
                      onChange={(e) => handleGoalChange(index, 'support_content', e.target.value)}
                      placeholder="目標達成のための具体的な支援内容"
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>頻度</Label>
                    <Input
                      value={goal.frequency}
                      onChange={(e) => handleGoalChange(index, 'frequency', e.target.value)}
                      placeholder="例: 週2回、毎日"
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* スケジュール・特記事項 */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle>スケジュール・特記事項</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>週間スケジュール</Label>
              <Textarea
                value={formData.weekly_schedule}
                onChange={(e) => handleChange('weekly_schedule', e.target.value)}
                placeholder="週間の活動スケジュールを記入"
                rows={4}
                data-testid="plan-schedule-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>特記事項</Label>
              <Textarea
                value={formData.special_notes}
                onChange={(e) => handleChange('special_notes', e.target.value)}
                placeholder="その他の特記事項、注意点など"
                rows={3}
                data-testid="plan-notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* 送信ボタン */}
        <div className="flex justify-end gap-4">
          <Button 
            type="button" 
            variant="outline"
            onClick={() => navigate('/support-plans')}
            data-testid="cancel-button"
          >
            キャンセル
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="btn-active"
            data-testid="submit-button"
          >
            <Save size={18} className="mr-2" />
            {loading ? '保存中...' : '計画を作成'}
          </Button>
        </div>
      </form>
    </div>
  );
}
